/* ============================================================
   Lab Effects — MOBILE APP PDP behaviour
   assets/le-mobile-pdp.js

   Plant-source + size switching, live pricing, gallery, qty, ATC,
   and the sticky jump bar. Reads the variant matrix the section
   emits as JSON — prices are formatted SERVER-SIDE so no currency
   logic lives here.

   Add to cart goes through window.LE_addToCart, the one add path
   for the whole storefront (CLAUDE.md). Never hand-roll another.
   ============================================================ */
(function () {
  'use strict';
  var root = document.querySelector('[data-le-mpdp]');
  if (!root || root.__leInit) return;
  root.__leInit = true;

  var blob = document.querySelector('[data-mpdp-variants]');
  if (!blob) return;
  var VARIANTS = [];
  try { VARIANTS = JSON.parse(blob.textContent); } catch (e) { return; }
  if (!VARIANTS.length) return;

  /* `extra` is the optional THIRD axis (next-gen's profile / blend picker).
     Products without one carry "default" for every variant, so the filters
     below need no special case. */
  var HAS_EXTRA = root.dataset.hasExtra === '1';
  /* SRC_CATS: each plant source is its own category (a merged product), so the
     whole page follows the selected source. '0' on Flavor Enhanced: the product
     is the category, the source is an attribute — the page keeps the category
     colour and only the source cards change. Set by the section; see the
     src_cats note in le-mobile-pdp.liquid. */
  var SRC_CATS = root.dataset.srcCats !== '0';
  var state = {
    src: root.dataset.src || VARIANTS[0].src,
    size: null,
    extra: HAS_EXTRA ? VARIANTS[0].extra : 'default',
    qty: 1
  };

  function q(sel, ctx) { return (ctx || root).querySelector(sel); }
  function all(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  /* The catalogue is priced in whole dollars, so ".00" is pure noise on the
     biggest number on the card. Cents still render if a price ever has them. */
  function money(cents) {
    var v = cents / 100;
    var whole = v % 1 === 0;
    return '$' + v.toLocaleString('en-US', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2
    });
  }

  function forSrc(src) {
    return VARIANTS.filter(function (v) {
      return v.src === src && (!HAS_EXTRA || v.extra === state.extra);
    });
  }
  function find(src, size) {
    var list = forSrc(src);
    for (var i = 0; i < list.length; i++) if (list[i].size === size) return list[i];
    return null;
  }
  /* Cheapest AVAILABLE variant for a source — what the source button prices at,
     so the three buttons compare like for like. */
  function leadFor(src) {
    var list = forSrc(src).filter(function (v) { return v.available; });
    if (!list.length) list = forSrc(src);
    return list.slice().sort(function (a, b) { return a.price - b.price; })[0] || null;
  }
  /* Keep the chosen size when switching source; fall back to that source's
     cheapest only if the size genuinely does not exist there. */
  function resolve() {
    if (!state.size) {
      var lead = leadFor(state.src);
      state.size = lead ? lead.size : null;
    }
    var v = find(state.src, state.size);
    if (!v) {
      var l = leadFor(state.src);
      state.size = l ? l.size : null;
      v = l;
    }
    return v;
  }

  /* Assigned by mountExtraSelect() below. Declared up here because paint()
     reads it, and paint() is called from handlers that can fire first. */
  var extraSel = null;

  /* CONTRAST-AWARE INK FOR THE ACCENT.

     Chips, the size tag and the option dropdown are all filled with the
     product's category colour, and their text was hardcoded #fff. Fine on the
     dark categories, unreadable on the pale ones — white on the sample kits'
     #9bc68e is 1.9:1, which is what prompted this.

     Decided from the colour, not per product, so every category is handled and
     nobody has to remember to set a text colour beside a background.

     WHITE IS THE DEFAULT and only loses when it genuinely fails: the rule is
     the real WCAG contrast ratio, and ink is used only when white drops below
     3:1 (the large-text minimum — these are 14px bold and up). A single
     luminance threshold was tried first and got gold wrong, leaving #DAA423 on
     white at 2.3:1. Measured against the live palette:

       kits  #9bc68e  white 1.9:1  -> ink
       gold  #DAA423  white 2.3:1  -> ink
       leaf  #60B050  white 2.7:1  -> ink
       #609e48        white 3.3:1  -> white  (Blue Dream and friends unchanged)
       teal  #108090  white 4.7:1  -> white
       CBS   #C0392B  white 5.4:1  -> white

     So only the three that actually fail flip; every dark category keeps the
     look it has today. */
  var INK = '#072326';
  function luminance(hex) {
    var c = String(hex || '').trim().replace('#', '');
    if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    if (!/^[0-9a-fA-F]{6}$/.test(c)) return null;
    var lin = function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * lin(parseInt(c.slice(0, 2), 16)) +
           0.7152 * lin(parseInt(c.slice(2, 4), 16)) +
           0.0722 * lin(parseInt(c.slice(4, 6), 16));
  }
  function inkOn(hex) {
    var L = luminance(hex);
    if (L === null) return '#fff';
    var vsWhite = 1.05 / (L + 0.05);     // white luminance is 1
    return vsWhite < 3 ? INK : '#fff';
  }

  function paint() {
    var v = resolve();
    if (!v) return;

    all('[data-src-btn]').forEach(function (b) {
      var s = b.getAttribute('data-src-btn');
      var on = s === state.src;
      var acc = b.getAttribute('data-accent') || '#108090';
      b.classList.toggle('on', on);
      b.style.background = on ? acc : '';
      b.style.borderColor = on ? acc : '';
      /* BDT orange (#ef9132) fails white at 2.4:1, CDT green passes — same
         contrast rule as the size chips, so the label stays readable on
         whichever source colour is selected. */
      b.style.color = on ? inkOn(acc) : '';
      /* Price the button at the SELECTED SIZE, not that source's cheapest, so
         the three prices are comparable and the discount badges all move
         together when the shopper changes size. Falls back to the cheapest
         only when this source genuinely does not carry that size. */
      var shown = find(s, state.size) || leadFor(s);
      var pz = q('[data-src-price]', b);
      if (pz && shown) {
        pz.innerHTML = '<b>' + (shown.priceShort || shown.priceStr) + '</b>' +
          (shown.compare > shown.price ? '<s>' + (shown.compareShort || shown.compareStr) + '</s>' : '');
      }
      var off = q('[data-src-off]', b);
      if (off && shown) {
        var pct = shown.compare > shown.price ? Math.round((1 - shown.price / shown.compare) * 100) : 0;
        off.hidden = !pct;
        off.textContent = pct ? '-' + pct + '%' : '';
      }
    });

    /* Products with no plant-source axis render no source buttons, so the
       accent and category name come off the section root instead — otherwise
       every non-merged PDP painted generic teal with a blank category line. */
    var btn = q('[data-src-btn="' + state.src + '"]');
    /* `root` directly, NOT q('[data-le-mpdp]'). q() scopes its lookup to root,
       and an element is never its own descendant — so that returned null and
       every non-merged PDP fell back to the generic teal, painting size chips
       and the About row teal while the server-rendered category line beside
       them showed the real category colour. */
    var rootAccent = root.getAttribute('data-accent') || '#108090';
    /* The PAGE accent: the source's own colour on a merged product, the
       product category's colour on Flavor Enhanced (data-page-accent carries
       the right one either way). The source button itself always paints in
       its own data-accent above. */
    var accent = btn ? (btn.getAttribute('data-page-accent') || btn.getAttribute('data-accent') || rootAccent) : rootAccent;
    var catName = btn ? btn.getAttribute('data-catname')
                      : (root.getAttribute('data-catname') || '');
    var note = btn ? btn.getAttribute('data-note') : '';

    /* Only write the category line when we actually have a name: on a
       non-merged PDP it is server-rendered, and blanking it here would erase
       markup that is already correct. */
    var catLine = q('[data-cat-line]');
    if (catLine && catName && SRC_CATS) { catLine.textContent = catName; catLine.style.color = accent; }
    var noteEl = q('[data-srcnote]');
    if (noteEl) { noteEl.textContent = note || ''; noteEl.hidden = !note; }

    /* Size chips: a source may not carry every size. Disable rather than hide,
       so the range on offer stays legible. */
    var sizes = forSrc(state.src);
    all('[data-size-btn]').forEach(function (b) {
      var s = b.getAttribute('data-size-btn');
      var match = null;
      for (var i = 0; i < sizes.length; i++) if (sizes[i].size === s) match = sizes[i];
      var on = s === state.size;
      b.classList.toggle('on', on);
      b.disabled = !match || !match.available;
      b.style.background = on ? accent : '';
      b.style.borderColor = on ? accent : '';
      b.style.color = on ? inkOn(accent) : '';
    });

    /* Third-axis chips. Every variant of the chosen profile is considered, so
       a profile is only disabled when it is genuinely unavailable in every
       size rather than merely absent at the size currently selected. */
    if (HAS_EXTRA && extraSel) {
      // The dropdown owns its own display; keep its label and fill in step.
      if (state.extra) extraSel.setValue(state.extra);
      var mnt = document.querySelector('[data-extra-mount]');
      if (mnt) {
        mnt.style.setProperty('--ss-fill', accent);
        mnt.style.setProperty('--ss-ink', inkOn(accent));
      }
    }
    if (HAS_EXTRA) {
      all('[data-extra-btn]').forEach(function (b) {
        var k = b.getAttribute('data-extra-btn');
        var any = VARIANTS.filter(function (vv) { return vv.extra === k; });
        var on = k === state.extra;
        b.classList.toggle('on', on);
        b.disabled = !any.length || !any.some(function (vv) { return vv.available; });
        b.style.background = on ? accent : '';
        b.style.borderColor = on ? accent : '';
        b.style.color = on ? inkOn(accent) : '';
      });
    }

    var szEl = q('[data-sum-size]');
    if (szEl) {
      szEl.textContent = v.sizeLabel;
      szEl.style.background = accent;
      szEl.style.borderColor = accent;
      szEl.style.color = inkOn(accent);
    }
    var subEl = q('[data-sum-src]');
    if (subEl) {
      /* Under the product name in the buy card: the category on a merged
         product; on Flavor Enhanced the SELECTED SOURCE in its own colour, so
         the card says which of the two the shopper is about to buy. */
      if (btn && !SRC_CATS) {
        subEl.textContent = btn.getAttribute('data-catshort') || '';
        subEl.style.color = btn.getAttribute('data-accent') || accent;
      } else {
        subEl.textContent = catName; subEl.style.color = accent;
      }
    }

    var totEl = q('[data-sum-total]');
    if (totEl) {
      totEl.innerHTML = '<b>' + money(v.price * state.qty) + '</b>' +
        (v.compare > v.price ? '<s>' + money(v.compare * state.qty) + '</s>' : '');
    }
    var perEl = q('[data-sum-per]');
    if (perEl) {
      var ml = parseFloat(String(v.sizeLabel).replace(/[^\d.]/g, '')) || 0;
      var pct = v.compare > v.price ? Math.round((1 - v.price / v.compare) * 100) : 0;
      perEl.textContent = (ml ? money(Math.round(v.price / ml)) + '/ml' : '') + (pct ? ' · save ' + pct + '%' : '');
    }

    var qv = q('[data-qtyv]');
    if (qv) qv.textContent = state.qty;

    var atc = q('[data-mpdp-atc]');
    if (atc) {
      atc.disabled = !v.available;
      /* No price in the button. The running total is already the largest
         thing on the card directly above it; repeating it made the button
         wrap and said nothing new. */
      atc.textContent = v.available ? 'Add to cart' : 'Sold out';
      atc.dataset.variantId = v.id;
    }

    /* The stage gradient and the thumb tiles follow the selected source's
       colour — one variable, same as the desktop stage.

       The SLIDE is deliberately NOT swapped on source change: slide 0 is the
       twist card, the strain's illustration, identical for all three sources.
       Same as the desktop merged PDP. Do not "fix" that. */
    /* About pill follows the selection: colour, label and the data-about key
       the desktop trigger reads. */
    var ab = q('[data-aboutbtn]');
    if (ab) {
      ab.style.setProperty('--ab', accent);
      ab.setAttribute('data-about', state.src);
      var abl = q('[data-aboutbtn-label]', ab);
      /* SHORT name — "About Cannabis Derived", not "…Derived Terpenes". The
         long form overran the pill and slid under the arrow. */
      var shortName = btn ? btn.getAttribute('data-catshort') : '';
      if (abl) abl.textContent = 'About ' + (shortName || catName || 'this plant source');
    }

    // Swap the related rail to the selected source's category collection, and
    // point View all at that same collection.
    // document-scoped: le-mobile.js moves these rails out of the section root.
    Array.prototype.slice.call(document.querySelectorAll('[data-rel]')).forEach(function (r) {
      var key = r.getAttribute('data-rel');
      // One category -> one rail keyed 'default', whichever source is selected.
      var on = SRC_CATS ? key === state.src : key === 'default';
      r.hidden = !on;
      if (on) {
        var va = document.querySelector('[data-rel-viewall]');
        var url = r.getAttribute('data-rel-url');
        if (va && url) va.setAttribute('href', url);
      }
    });

    var gal = q('.gal');
    if (gal) gal.style.setProperty('--gc', accent);
    var thumbs = q('.thumbs');
    if (thumbs) thumbs.style.setProperty('--gc', accent);

    /* Flavor Enhanced "Made with" cards (moved into the Details accordion by
       adoptDesktop below): show the strain card for the selected plant source,
       the same rule le-pdp-story's own script applies to the desktop chips.
       The flavor card carries no data-mw-plant and always shows. */
    var want = state.src.indexOf('cannabis') !== -1 ? 'cannabis'
             : (state.src.indexOf('botanical') !== -1 ? 'botanical' : '');
    if (want) {
      Array.prototype.slice.call(document.querySelectorAll('[data-mw-plant]')).forEach(function (c) {
        c.hidden = c.getAttribute('data-mw-plant') !== want;
      });
    }
  }

  /* ---------- gallery slides ---------- */
  function slides() { return all('[data-slide]'); }

  /* Slide the tapped thumb to the left gutter and carry the row with it, so
     you can walk the whole set by tapping the same spot instead of pushing
     thumbs into the right wall. Measured off bounding rects rather than
     offsetLeft — .thumbs is not positioned, so offsetParent is somewhere up
     the tree and offsetLeft would be relative to the wrong box. */
  function scrollThumbIntoGutter(i) {
    var rail = q('[data-thumbs]');
    var t = q('[data-thumb="' + i + '"]');
    if (!rail || !t) return;
    var pad = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
    var delta = t.getBoundingClientRect().left - rail.getBoundingClientRect().left - pad;
    var target = rail.scrollLeft + delta;

    /* CLAMP. A target past the end makes scrollTo do NOTHING here rather than
       stop at the maximum — measured: left:574 against a 493 max left the rail
       at 0, so the last few thumbs never scrolled at all. le-pdp-gallery.js
       clamps for the same reason; this follows that. */
    var maxScroll = rail.scrollWidth - rail.clientWidth;
    if (target < 0) target = 0;
    if (target > maxScroll) target = maxScroll;

    /* Tweened by hand rather than handed to the platform.

       BOTH platform routes are a no-op in some engines, measured on this rail:
         scrollTo({left:200, behavior:'auto'})    -> 200  OK
         scrollTo({left:200, behavior:'smooth'})  ->   0  nothing happened
         rail.scrollLeft = 200                    -> 200  OK
       and once CSS `scroll-behavior: smooth` was set, the plain assignment
       became a no-op too — the smoothing, not the call, is what fails. So the
       rail is left on scroll-behavior:auto and eased here, which is the only
       version that provably moves. Reduced motion jumps straight there. */
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { rail.scrollLeft = target; return; }

    if (rail.__leTween) cancelAnimationFrame(rail.__leTween);
    clearTimeout(rail.__leTweenSafety);
    var from = rail.scrollLeft;
    var dist = target - from;
    if (!dist) return;

    var DUR = 320;
    var start = null;
    var settled = false;

    /* The tween is rAF-driven, and rAF DOES NOT FIRE in a backgrounded or
       hidden tab — the same starvation that stranded the add-to-cart toast.
       This guard guarantees the rail still ends up in the right place: if the
       frame loop never completes the tween, land it outright. Correct position
       always; the easing is the part that is allowed to be skipped. */
    var settle = function () {
      if (settled) return;
      settled = true;
      rail.__leTween = null;
      rail.scrollLeft = target;
    };

    var step = function (ts) {
      if (settled) return;
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / DUR);
      var eased = 1 - Math.pow(1 - t, 3);           // ease-out cubic
      rail.scrollLeft = from + dist * eased;
      if (t < 1) rail.__leTween = requestAnimationFrame(step);
      else settle();
    };

    rail.__leTween = requestAnimationFrame(step);
    rail.__leTweenSafety = setTimeout(settle, DUR + 120);
  }

  function showSlide(i, fromTap) {
    var list = slides();
    if (!list.length) return;
    if (i < 0) i = list.length - 1;
    if (i >= list.length) i = 0;
    list.forEach(function (el, n) { el.classList.toggle('is-on', n === i); });
    all('[data-thumb]').forEach(function (t) {
      var on = parseInt(t.getAttribute('data-thumb'), 10) === i;
      t.classList.toggle('on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    all('[data-dots] i').forEach(function (d, n) { d.classList.toggle('on', n === i); });
    if (fromTap) scrollThumbIntoGutter(i);
  }

  function currentSlide() {
    var list = slides();
    for (var i = 0; i < list.length; i++) if (list[i].classList.contains('is-on')) return i;
    return 0;
  }

  /* ---------- interactions ---------- */
  root.addEventListener('click', function (e) {
    var s = e.target.closest('[data-src-btn]');
    if (s) { state.src = s.getAttribute('data-src-btn'); paint(); return; }

    var z = e.target.closest('[data-size-btn]');
    if (z && !z.disabled) { state.size = z.getAttribute('data-size-btn'); paint(); return; }

    var x = e.target.closest('[data-extra-btn]');
    if (x && !x.disabled) {
      state.extra = x.getAttribute('data-extra-btn');
      /* Sizes are per profile on these products, so a size that does not exist
         under the new profile must not be carried across. resolve() picks that
         profile's lead variant when the current size has no match. */
      if (!find(state.src, state.size)) state.size = null;
      paint();
      return;
    }

    var qb = e.target.closest('[data-qty]');
    if (qb) { state.qty = Math.max(1, state.qty + parseInt(qb.getAttribute('data-qty'), 10)); paint(); return; }

    var th = e.target.closest('[data-thumb]');
    if (th) { showSlide(parseInt(th.getAttribute('data-thumb'), 10), true); return; }

    /* Kept although the arrows were removed: if a [data-gal] control is ever
       reinstated it works with no further wiring. */
    var arrow = e.target.closest('[data-gal]');
    if (arrow) { showSlide(currentSlide() + parseInt(arrow.getAttribute('data-gal'), 10), true); return; }

    if (e.target.closest('[data-jump-top]')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    var atc = e.target.closest('[data-mpdp-atc]');
    if (atc && !atc.disabled) {
      var id = atc.dataset.variantId;
      if (!id || typeof window.LE_addToCart !== 'function') return;
      atc.disabled = true;
      var was = atc.textContent;
      atc.textContent = 'Adding…';
      window.LE_addToCart(id, state.qty)
        .then(function () { atc.textContent = 'Added \u2713'; setTimeout(function () { atc.disabled = false; paint(); }, 1200); })
        .catch(function () { atc.textContent = was; atc.disabled = false; });
      return;
    }

    /* NOTE: there is deliberately no [data-open-about] handler here.
       le-merged-pdp.js already listens for that selector on the document, so
       re-dispatching a click ran openAbout() twice — two LESheetLock.lock()
       calls against one unlock, which left the page locked with a stale scroll
       offset. The About pill uses .le-ps__about[data-about] and is handled
       entirely by that same desktop listener. */
  });

  /* ---------- sticky jump bar ---------- */
  var jump = q('[data-jump]');
  var buycard = q('.buycard');
  if (jump && buycard && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      jump.classList.toggle('show', entries[0].boundingClientRect.top < 0);
    }, { threshold: 0 }).observe(buycard);
  }

  /* ---------------------------------------------------------
     SEARCHABLE DROPDOWN for the third-axis option.

     Products flagged with custom.variant_dropdown (or the variant-dropdown
     tag) render a mount instead of the chip row — the same flag and the same
     component the desktop buybox uses, so the two cannot drift apart.

     The sample kits are why: eight options whose labels are full sentences
     ("5 Earthy/Mossy Strains (2ml each)") overlapped into unreadable mush in
     a chip row on a phone.

     `v` is the option HANDLE, matching what the chips put in
     [data-extra-btn], so state.extra means the same thing on both paths and
     nothing downstream needs a special case.
     --------------------------------------------------------- */
  (function mountExtraSelect(tries) {
    tries = tries || 0;
    var mount = document.querySelector('[data-extra-mount]');
    if (!mount) return;
    /* LOAD-ORDER RACE. le-sample-select.js is loaded from layout/theme.liquid
       at the end of <body>; this file is emitted by the section and therefore
       appears EARLIER in the document. Both are `defer`, and defer executes in
       document order — so LE_sampleSelect does not exist yet on the first pass
       and the mount silently did nothing. Wait for it, bounded, the same way
       the chat and reviews bridges wait for their third-party globals. */
    if (!window.LE_sampleSelect) {
      if (tries < 40) setTimeout(function () { mountExtraSelect(tries + 1); }, 50);
      return;
    }
    var vals;
    try { vals = JSON.parse(mount.getAttribute('data-values') || '[]'); } catch (err) { return; }
    if (!vals.length) return;
    extraSel = window.LE_sampleSelect(mount, {
      samples: vals,
      value: state.extra || null,
      placeholder: 'Choose an option',
      onChange: function (v) {
        state.extra = v;
        // Sizes are per option on these products, so one that does not exist
        // under the newly chosen option must not be carried across.
        if (!find(state.src, state.size)) state.size = null;
        paint();
      }
    });
    /* Repaint now that extraSel exists. The wait above means the mount can land
       AFTER the initial paint(), and the accent fill/ink for this control are
       applied from paint() — without this the dropdown keeps its fallback
       translucent styling until the shopper touches something else. */
    paint();
  })();

  /* =========================================================
     DESKTOP CONTENT -> MOBILE ACCORDIONS

     The essentials story (the-cut / base / thick / clear), the next-gen
     format story (water-soluble / flowable / beer-spirits), the kit
     contents and the Flavor Enhanced "Made with" cards are all rendered
     ONCE, by their desktop sections, from editor content this section
     cannot read (another section's settings are not visible in Liquid).
     Below 768px those sections are display:none, so the phone lost
     roughly 500 words of the most useful copy on those products.

     This MOVES those server-rendered nodes — never copies them — into
     <details> rows inside the Details accordion, one row per desktop
     band (the bands are separated by <hr>). The text exists once in the
     HTML either way: crawlers without JS see it in the desktop section,
     Google's renderer sees it in the accordion. Above 768px everything
     is put back where it came from, so desktop is untouched.

     The single <h1> works the same way: the desktop section owns it and
     it is swapped into the title slot here on phones (see the note on
     [data-title-slot] in the section).
     ========================================================= */
  var MQ = window.matchMedia('(max-width: 767px)');
  var moved = [];          // { node, parent, next } — home addresses for restore
  var built = [];          // <details> rows this created
  var titleSlot = null, titleH1 = null;

  function remember(node) { moved.push({ node: node, parent: node.parentNode, next: node.nextSibling }); }

  function makeRow(title, nodes, open, cls) {
    var d = document.createElement('details');
    d.className = 'le-madopt' + (cls ? ' ' + cls : '');
    if (open) d.open = true;
    var sm = document.createElement('summary');
    sm.textContent = title;
    d.appendChild(sm);
    var body = document.createElement('div');
    body.className = 'body le-madopt__body';
    d.appendChild(body);
    nodes.forEach(function (n) { remember(n); body.appendChild(n); });
    built.push(d);
    return d;
  }

  /* Split a band container's children into segments at its rule elements. */
  function segments(container, ruleSel) {
    var segs = [], cur = [];
    Array.prototype.slice.call(container.children).forEach(function (c) {
      if (c.matches(ruleSel)) { if (cur.length) segs.push(cur); cur = []; return; }
      cur.push(c);
    });
    if (cur.length) segs.push(cur);
    return segs;
  }
  function has(seg, sel) {
    return seg.some(function (n) { return n.matches(sel) || !!n.querySelector(sel); });
  }
  var TITLE_SEL = 'h3.le-pdp-ess__h2, .le-pdp-ess__formula-h, p.le-pdp-ess__sub, h3.h2, p.sublabel';
  function titleOf(seg, fallback) {
    var el = null;
    seg.some(function (n) { el = n.matches(TITLE_SEL) ? n : n.querySelector(TITLE_SEL); return !!el; });
    if (el && el.textContent.trim()) {
      el.classList.add('le-madopt__used');   // it became the row's summary
      return el.textContent.trim();
    }
    return fallback;
  }
  function fallbackFor(seg) {
    if (has(seg, '.le-pdp-ess__why, .intro')) return 'About this product';
    if (has(seg, '#le-pdp-howto, #le-nx-howto, .le-pdp-ess__steps, .prins')) return 'How to use';
    if (has(seg, '.le-pdp-ess__formula')) return 'Formulation example';
    if (has(seg, '.le-pdp-ess__feat')) return 'Features';
    if (has(seg, '.le-pdp-ess__facts, .facts')) return 'The facts';
    if (has(seg, '.le-pdp-ess__excl')) return 'Contains none of';
    if (has(seg, '.le-pdp-ess__bens')) return 'Key benefits';
    if (has(seg, '.cats')) return 'Every profile, in this format';
    if (has(seg, '.apps')) return 'Common applications';
    if (has(seg, '.kf')) return 'Key factors';
    if (has(seg, '.others')) return 'Also available in these formats';
    return 'Details';
  }

  function adoptDesktop() {
    if (!MQ.matches || built.length) return;
    var acc = q('.acc[data-le-acc-group="mpdp"]');
    if (!acc) return;
    var terps = q('[data-acc-terps]', acc);
    var desc  = q('[data-acc-desc]', acc);
    var first = acc.firstElementChild;

    /* 1. Kit contents — first row, open: it is what a kit page is for. */
    var kit = document.querySelector('.le-pdp-story .le-kit');
    if (kit) {
      var kh = kit.querySelector('.le-kit__h');
      var kt = kh ? kh.textContent.trim() : "What's in each kit";
      if (kh) kh.classList.add('le-madopt__used');
      acc.insertBefore(makeRow(kt, [kit], true, 'le-madopt--kit'), first);
      first = acc.firstElementChild;
    }

    /* 2. Flavor Enhanced "Made with" — above Dominant terpenes, open on load;
          the terpenes row closes so only one row starts open. */
    var mw = document.querySelector('.le-pdp-story .le-pdp-madewith');
    if (mw && mw.children.length) {
      acc.insertBefore(makeRow('Made with', [mw], true, 'le-madopt--mw'), first);
      if (terps) terps.open = false;
    }

    /* 3. Essentials / next-gen bands -> one row each, after the description. */
    var bands = [];
    var ess = document.querySelector('.le-pdp-ess .le-pdp-ess__in');
    if (ess) {
      segments(ess, 'hr.le-pdp-ess__hr').forEach(function (seg) {
        if (has(seg, '.le-pdp-ess__head')) return;          // name + tagline: the page has these
        if (has(seg, '.le-pdp-ess__docs-certs')) return;    // COA/SDS row already exists here
        bands.push(seg);
      });
    }
    var nx = document.querySelector('.nx-in');
    if (nx) {
      segments(nx, 'hr.soft').forEach(function (seg) {
        if (has(seg, 'h2.pname')) return;                  // header: name + tagline
        if (has(seg, '.docs-certs')) return;               // COA/SDS row already exists here
        bands.push(seg);
      });
    }
    var cursor = desc ? desc.nextSibling : (terps ? terps.nextSibling : acc.firstChild);
    bands.forEach(function (seg) {
      var row = makeRow(titleOf(seg, fallbackFor(seg)), seg, false, 'le-madopt--band');
      acc.insertBefore(row, cursor);
    });
    paint();   // the made-with toggle reads the moved cards
  }

  /* ONE <h1>: swap the desktop heading element into the mobile title slot. */
  function adoptTitle() {
    if (!MQ.matches || titleH1) return;
    var slot = q('[data-title-slot]');
    var h1 = document.querySelector('#mpdp h1[data-title], .le-pdp-hero h1');
    if (!slot || !h1) return;
    remember(h1);
    titleSlot = slot; titleH1 = h1;
    h1.classList.add('pd__title');
    slot.parentNode.replaceChild(h1, slot);
  }

  /* Desktop again (a rotated tablet crossing 768px): put everything back. */
  function restoreDesktop() {
    if (titleH1 && titleSlot) {
      titleH1.classList.remove('pd__title');
      titleH1.parentNode.replaceChild(titleSlot, titleH1);
    }
    moved.slice().reverse().forEach(function (m) {
      var next = (m.next && m.next.parentNode === m.parent) ? m.next : null;
      m.parent.insertBefore(m.node, next);
    });
    Array.prototype.slice.call(document.querySelectorAll('.le-madopt__used')).forEach(function (el) { el.classList.remove('le-madopt__used'); });
    built.forEach(function (d) { if (d.parentNode) d.parentNode.removeChild(d); });
    moved = []; built = []; titleH1 = null; titleSlot = null;
  }

  function onMQ() {
    if (MQ.matches) { adoptTitle(); adoptDesktop(); }
    else if (built.length || titleH1) restoreDesktop();
  }
  if (MQ.addEventListener) MQ.addEventListener('change', onMQ); else MQ.addListener(onMQ);
  /* Belt and braces: a debounced resize check too. matchMedia 'change' did not
     fire under emulated viewport changes in testing; onMQ is idempotent, so
     running it from both sources costs nothing. */
  var rsT = null;
  window.addEventListener('resize', function () { clearTimeout(rsT); rsT = setTimeout(onMQ, 120); });

  /* ---------- gallery swipe ----------
     Drag the stage left or right to walk the images one at a time. The thumb
     rail and the dots already follow showSlide(), and showSlide() already wraps
     past both ends, so a swipe off the last image lands on the first — this
     only has to decide WHICH way and WHETHER the drag counted.

     ONE STEP PER GESTURE, never momentum. There is no velocity carry-over and
     no multi-slide fling: release, and it either completes the one step or
     springs back. That is what was asked for and it is also what suits a
     9-image maximum — a loose carousel would overshoot the set.

     The slides are absolutely stacked and toggled with display, not laid out on
     a track, so the drag borrows the neighbour: it is shown with .is-peek,
     parked one stage-width away on the side you are pulling from, and both move
     together under the finger. On release, whichever wins animates to rest,
     .is-peek comes off, and showSlide() does the real state change. Nothing
     about the display model changes, so the thumb tap path is untouched.

     touch-action:pan-y on the stage (see the CSS) is what makes this behave:
     the browser keeps vertical scrolling for itself and hands us horizontal, so
     every listener here stays passive and the page never fights the gesture.
     Do NOT add preventDefault — with pan-y it is unnecessary, and a
     non-passive touchmove on the hero is a scroll-jank regression.

     The axis is decided once per gesture, after 8px of travel, and a gesture
     that starts vertical is abandoned outright — so a shopper scrolling the
     page THROUGH the image never drags it sideways by accident. */
  (function initGallerySwipe() {
    var stage = q('.gal .stage');
    if (!stage) return;

    var STEP = 0.18;   /* fraction of the stage width that counts as a step */
    var FLICK = 0.45;  /* px/ms — a fast flick counts even when it is short */
    var MIN = 30;      /* px — but never shorter than this, twitch guard */
    var DUR = 260;

    var cur = null, peek = null, W = 0;
    var x0 = 0, y0 = 0, dx = 0, t0 = 0;
    var tracking = false, decided = false, horizontal = false, dir = 0;

    function at(i) {
      var list = slides();
      if (!list.length) return null;
      if (i < 0) i = list.length - 1;
      if (i >= list.length) i = 0;
      return list[i];
    }

    function reset() {
      slides().forEach(function (el) {
        el.style.transform = ''; el.style.transition = '';
        el.classList.remove('is-peek');
      });
      stage.classList.remove('is-dragging');
      cur = peek = null;
      tracking = decided = horizontal = false; dir = 0;
    }

    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      if (slides().length < 2) return;
      reset();
      W = stage.getBoundingClientRect().width || 1;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      dx = 0; t0 = Date.now(); tracking = true;
      cur = slides()[currentSlide()];
    }, { passive: true });

    stage.addEventListener('touchmove', function (e) {
      if (!tracking || e.touches.length !== 1) return;
      dx = e.touches[0].clientX - x0;
      var dy = e.touches[0].clientY - y0;

      if (!decided) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        decided = true;
        horizontal = Math.abs(dx) > Math.abs(dy);
        if (!horizontal) { tracking = false; return; }   /* their gesture is a scroll */
        stage.classList.add('is-dragging');
      }
      if (!horizontal) return;

      var want = dx < 0 ? 1 : -1;
      if (want !== dir) {
        if (peek && peek !== cur) { peek.classList.remove('is-peek'); peek.style.transform = ''; }
        dir = want;
        peek = at(currentSlide() + dir);
        if (peek && peek !== cur) peek.classList.add('is-peek');
      }
      if (cur) cur.style.transform = 'translateX(' + dx + 'px)';
      if (peek && peek !== cur) peek.style.transform = 'translateX(' + (dx + (dir > 0 ? W : -W)) + 'px)';
    }, { passive: true });

    function release() {
      if (!tracking && !horizontal) { reset(); return; }
      if (!horizontal) { reset(); return; }

      var speed = Math.abs(dx) / Math.max(1, Date.now() - t0);
      /* A flick counts even when it is short, but it still has to BE a swipe:
         without the distance floor a fast 20px twitch advanced the gallery,
         which is how a shopper adjusting their grip loses their place. */
      var commit = Math.abs(dx) > W * STEP || (speed > FLICK && Math.abs(dx) > MIN);
      var from = currentSlide();
      /* Captured, because reset() clears `dir` and it runs first in the timeout
         below — reading dir there gave `from + 0` and every swipe sprang back. */
      var step = dir;
      var ease = 'transform ' + DUR + 'ms cubic-bezier(.22,.61,.36,1)';

      if (cur) { cur.style.transition = ease; cur.style.transform = 'translateX(' + (commit ? (dir > 0 ? -W : W) : 0) + 'px)'; }
      if (peek && peek !== cur) {
        peek.style.transition = ease;
        peek.style.transform = 'translateX(' + (commit ? 0 : (dir > 0 ? W : -W)) + 'px)';
      }

      /* Let the animation finish before the display swap, or the incoming slide
         blinks from display:none to display:flex mid-travel. */
      setTimeout(function () {
        reset();
        if (commit) showSlide(from + step, true);
      }, DUR);

      tracking = false;
    }

    stage.addEventListener('touchend', release, { passive: true });
    stage.addEventListener('touchcancel', function () { reset(); }, { passive: true });
  })();

  paint();
  adoptTitle();
  adoptDesktop();
})();
